import { useRoute, Link } from "wouter";
import { useGetMe, useGetCompanyBySlug, useSelectCompany, useListCompanies, getGetMeQueryKey, getListCompaniesQueryKey } from "@workspace/api-client-react";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import IntegrationsTab from "./IntegrationsTab";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, Loader2, AlertCircle } from "lucide-react";

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
    </div>
  );
}

export default function IntegrationsPage() {
  const [, params] = useRoute("/app/:slug/integrations");
  const slug = params?.slug ?? "";
  const { data: user, isLoading: userLoading } = useGetMe();
  const { data: companyInfo, isLoading: slugLoading } = useGetCompanyBySlug(slug, {
    query: { enabled: !!slug && !!user },
  });
  const { data: companies = [] } = useListCompanies({ query: { enabled: !!user } });
  const selectCompany = useSelectCompany();
  const qc = useQueryClient();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [switching, setSwitching] = useState(false);
  const switchingRef = useRef(false);
  const toastShownRef = useRef(false);

  useEffect(() => {
    if (toastShownRef.current) return;
    const sp = new URLSearchParams(window.location.search);
    const connected = sp.get("integration_connected");
    const error = sp.get("integration_error");
    if (connected || error) {
      toastShownRef.current = true;
      window.history.replaceState({}, "", window.location.pathname);
      if (connected) {
        const name = connected === "quickbooks" ? "QuickBooks Online"
          : connected === "xero" ? "Xero"
          : connected === "zoho" ? "Zoho Books"
          : connected;
        toast({ title: `${name} connected`, description: "Your accounting platform has been linked successfully." });
      } else if (error) {
        toast({ title: "Connection failed", description: `Could not connect: ${error}`, variant: "destructive" });
      }
    }
  }, []);

  useEffect(() => {
    if (!companyInfo || !user) return;
    if (user.companyId !== companyInfo.id && !switchingRef.current) {
      switchingRef.current = true;
      setSwitching(true);
      selectCompany.mutateAsync({ data: { companyId: companyInfo.id } })
        .then(() => Promise.all([
          qc.invalidateQueries({ queryKey: getGetMeQueryKey() }),
          qc.invalidateQueries({ queryKey: getListCompaniesQueryKey() }),
        ]))
        .catch(() => setLocation("/cp"))
        .finally(() => { switchingRef.current = false; setSwitching(false); });
    }
  }, [user?.companyId, companyInfo?.id]);

  if (userLoading || slugLoading || switching) return <LoadingScreen />;
  if (!user) { setLocation("/app/login"); return null; }

  const companyId = companyInfo?.id ?? user.companyId ?? "";
  const currentCompany = companies.find((c) => c.id === companyId);
  const memberRole = (currentCompany as unknown as { memberRole?: string })?.memberRole;
  const isAdmin = user.role === "Admin" || user.role === "Super Admin"
    || memberRole === "Admin" || memberRole === "Owner";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="h-16 border-b border-border/50 bg-card flex items-center justify-between px-6 sm:px-10 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <img src="/images/finproject-logo.png" alt="FinProject" className="h-8 w-auto rounded-lg" style={{ border: "2px solid #e2e8f0", padding: "2px" }} />
          <span className="font-bold text-base hidden sm:inline">{companyInfo?.name ?? "FinProject"}</span>
          <span className="text-muted-foreground">/</span>
          <span className="font-semibold text-sm">Integrations</span>
        </div>
        <Link
          href={`/app/${slug}`}
          className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Portal
        </Link>
      </header>

      <main className="flex-1 p-6 sm:p-10 overflow-auto">
        <div className="max-w-5xl mx-auto">
          {!isAdmin ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center text-muted-foreground">
                <AlertCircle className="w-10 h-10 mx-auto mb-3 text-muted-foreground/50" />
                <p className="font-medium">Admin access required</p>
                <p className="text-sm mt-1">Only company admins can manage integrations.</p>
              </div>
            </div>
          ) : companyId ? (
            <IntegrationsTab companyId={companyId} isAdmin={isAdmin} />
          ) : (
            <LoadingScreen />
          )}
        </div>
      </main>
    </div>
  );
}
